import nlp from 'compromise/two';
import { cloneDeep, intersection } from 'lodash';
import { nanoid } from 'nanoid';

import {
  ALL_SPECIAL_CHARACTERS_REGEX,
  BOUNDARY_SPECIAL_EXTRACT_REGEX,
  COMPOUND_WORD_REGEX,
  COMPOUNDED_ADJECTIVE_REGEX,
  END_SINGLE_QUOTE_REGEX,
  MULTIPLE_SPECIAL_CHARACTERS,
  NUMBER_REGEX,
  PHONE_NUMBER_REGEX,
  PUNCTUATION_REGEX,
  SPACES_REGEX,
  START_SINGLE_QUOTE_REGEX,
  THREE_DOT_REGEX,
  transliterate,
} from './regex';
import { expandAbbreviationToFullWord, isAbbreviation } from './abbreviation';

export interface OptionItem {
  label: string;
  value: any;
  disabled?: boolean;
}

export type TextItemModel = {
  key?: string;
  value?: string;
  children?: TextItemModel[];
};

export const wordCount = (text: string) => {
  return nlp(text).wordCount();
};

export const wordCountIncludeDash = (text: string) => {
  return (text || '')
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
};

export const splitSentenceUnicodeToWords = (sentence) => {
  let words = [];
  // Split sentence to words by one or many space chars.
  // Ex: In 18th- and 19th-century, it was the 'burst' of times - Mc'Donald tree's trees' leaves "this is an example" leaves this is £23.70 100% e-ticket pre-parses S-T-A-U-N-F-I-R-T-H? We'd 'seating' OK – so café (800)555-555 and (800) 555 - 555... ‘Het case roi nhe!’
  // Will be split by space into: [it], [the], ['burst'], [-], [Mc'Donald], ["this is an example"], [£23.70], ...
  if (sentence) {
    const wordTerms = nlp(transliterate(sentence)).termList();

    words = wordTerms
      ?.flatMap((eachTerm) => [eachTerm.pre.trim(), eachTerm.text.trim(), eachTerm.post.trim()])
      .filter(Boolean);
  }
  return words;
};

const splitWordByPattern = (pattern, word) => {
  const resultWords = [];
  const subWords = word.split(pattern);
  if (subWords.length > 0) {
    subWords.forEach((subWord) => {
      if (subWord) {
        resultWords.push(subWord);
      }
    });
  }
  return resultWords;
};

export function splitToWordsForExercise(sentence) {
  // Use compromise to expand contractions and normalize text
  const doc = nlp(sentence);

  // Expand contractions
  doc.contractions().expand();

  // Get the processed text
  let processed = doc.text();

  // Normalize accented characters
  processed = processed.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Replace all punctuation marks with spaces around them
  processed = processed.replace(/([^\w\s])/g, ' $1 ');

  // Split on whitespace and filter out empty strings
  const words = processed.split(/\s+/).filter((word) => word.length > 0);

  const mapWords = words.map((w) => {
    if (w.toLowerCase() === 'tirring') {
      return w.replace('rr', 'r');
    }
    return w;
  });

  return mapWords;
}

export const splitSentenceToWords = (sentence) => {
  const words = [];
  let isStartSingleQuote = false;
  // Split sentence to words by one or many space chars.
  // Ex: In 18th- and 19th-century, it was the 'burst' of times - Mc'Donald tree's trees' leaves "this is an example" leaves this is £23.70 100% e-ticket pre-parses S-T-A-U-N-F-I-R-T-H? We'd 'seating' OK – so café (800)555-555 and (800) 555 - 555... ‘Het case roi nhe!’
  // Will be split by space into: [it], [the], ['burst'], [-], [Mc'Donald], ["this is an example"], [£23.70], ...
  if (sentence) {
    const rawWords = transliterate(sentence).split(SPACES_REGEX) ?? [];
    rawWords.forEach((word) => {
      if (START_SINGLE_QUOTE_REGEX.test(word)) {
        // Mark there is a start single quote.
        isStartSingleQuote = true;
      }
      if (
        (END_SINGLE_QUOTE_REGEX.test(word) && !isStartSingleQuote) ||
        COMPOUNDED_ADJECTIVE_REGEX.test(word)
      ) {
        // Not split raw word to sub-words when word
        // - is end of quote and there is no start start of quote. Ex: [trees']
        // - end with the "-". Ex: [18th-].
        words.push(word);
      } else {
        if (END_SINGLE_QUOTE_REGEX.test(word)) {
          // Reset the start single quote mark.
          isStartSingleQuote = false;
        }

        // Split word to sub words if there is any boundary special character.
        // Ex: The word is ['burst']
        // Will be split to sub words: ['], [burst], ['].
        const subWords = BOUNDARY_SPECIAL_EXTRACT_REGEX.exec(word);
        (subWords ?? []).forEach((sw: string, index) => {
          // Ignore first match because it is the full string of characters matched.
          const isNotEmpty = (str: string) => typeof str === 'string' && str.trim().length > 0;

          if (index > 0 && isNotEmpty(sw)) {
            if (MULTIPLE_SPECIAL_CHARACTERS.test(sw) && !THREE_DOT_REGEX.test(sw)) {
              // Split the text contain all of multiple special characters.
              // Ex: Thw word [!’]
              // Will be split to [!], [’].
              words.push(...sw.split(''));
            } else if (THREE_DOT_REGEX.test(sw)) {
              // Split word to sub word by 3 dots.
              // Ex: The word [555...]
              // Will be split to sub words: [555], [...].
              words.push(...splitWordByPattern(THREE_DOT_REGEX, sw));
            } else if (
              PUNCTUATION_REGEX.test(sw) ||
              NUMBER_REGEX.test(sw) ||
              (COMPOUND_WORD_REGEX.test(sw) && !PHONE_NUMBER_REGEX.test(sw))
            ) {
              // Don't split word to sub word with cases word is:
              // - PUNCTUATION_REGEX. Ex: [-]
              // - NUMBER_REGEX. Ex: [23.70]
              // - PUNCTUATION_REGEX. Ex: [e-ticket]
              words.push(sw);
            } else if (ALL_SPECIAL_CHARACTERS_REGEX.test(sw)) {
              // Split word to sub word by special characters (includes the delimiters).
              // Ex: The word [800)555-555]
              // Will be split to sub words: [800], [)], [555], [-], [555].
              words.push(...splitWordByPattern(ALL_SPECIAL_CHARACTERS_REGEX, sw));
            } else {
              words.push(sw);
            }
          }
        });
      }
    });
  }
  return words;
};

export const simpleSplitSentenceToWords = (sentence) => {
  const words = [];

  let isStartSingleQuote = false;
  // Split sentence to words by one or many space chars.
  // Ex: In 18th- and 19th-century, it was the 'burst' of times - Mc'Donald tree's trees' leaves "this is an example" leaves this is £23.70 100% e-ticket pre-parses S-T-A-U-N-F-I-R-T-H? We'd 'seating' OK – so café (800)555-555 and (800) 555 - 555... ‘Het case roi nhe!’
  // Will be split by space into: [it], [the], ['burst'], [-], [Mc'Donald], ["this is an example"], [£23.70], ...
  if (sentence) {
    const rawWords = transliterate(sentence).split(SPACES_REGEX) ?? [];
    rawWords.forEach((word) => {
      if (START_SINGLE_QUOTE_REGEX.test(word)) {
        // Mark there is a start single quote.
        isStartSingleQuote = true;
      }
      if (
        (END_SINGLE_QUOTE_REGEX.test(word) && !isStartSingleQuote) ||
        COMPOUNDED_ADJECTIVE_REGEX.test(word)
      ) {
        // Not split raw word to sub-words when word
        // - is end of quote and there is no start start of quote. Ex: [trees']
        // - end with the "-". Ex: [18th-].
        words.push(word);
      } else {
        if (END_SINGLE_QUOTE_REGEX.test(word)) {
          // Reset the start single quote mark.
          isStartSingleQuote = false;
        }

        // Split word to sub words if there is any boundary special character.
        // Ex: The word is ['burst']
        // Will be split to sub words: ['], [burst], ['].
        const subWords = BOUNDARY_SPECIAL_EXTRACT_REGEX.exec(word);
        (subWords ?? []).forEach((sw: string, index) => {
          // Ignore first match because it is the full string of characters matched.
          const isNotEmpty = (str: string) => typeof str === 'string' && str.trim().length > 0;

          if (index > 0 && isNotEmpty(sw)) {
            if (MULTIPLE_SPECIAL_CHARACTERS.test(sw) && !THREE_DOT_REGEX.test(sw)) {
              // Split the text contain all of multiple special characters.
              // Ex: Thw word [!’]
              // Will be split to [!], [’].
              words.push(...sw.split(''));
            } else if (THREE_DOT_REGEX.test(sw)) {
              // Split word to sub word by 3 dots.
              // Ex: The word [555...]
              // Will be split to sub words: [555], [...].
              words.push(...splitWordByPattern(THREE_DOT_REGEX, sw));
            } else if (
              PUNCTUATION_REGEX.test(sw) ||
              NUMBER_REGEX.test(sw) ||
              (COMPOUND_WORD_REGEX.test(sw) && !PHONE_NUMBER_REGEX.test(sw))
            ) {
              // Don't split word to sub word with cases word is:
              // - PUNCTUATION_REGEX. Ex: [-]
              // - NUMBER_REGEX. Ex: [23.70]
              // - PUNCTUATION_REGEX. Ex: [e-ticket]
              words.push(sw);
            } else if (ALL_SPECIAL_CHARACTERS_REGEX.test(sw)) {
              // Split word to sub word by special characters (includes the delimiters).
              // Ex: The word [800)555-555]
              // Will be split to sub words: [800], [)], [555], [-], [555].
              words.push(...splitWordByPattern(ALL_SPECIAL_CHARACTERS_REGEX, sw));
            } else {
              words.push(sw);
            }
          }
        });
      }
    });
  }
  return words;
};

export const isPunctuation = (word) => {
  const normalizedWord = nlp(word)
    .terms()
    .json({ normal: true })
    .map((t) => t.normal)?.[0];

  return PUNCTUATION_REGEX.test(normalizedWord);
};

export const isSpace = (word) => SPACES_REGEX.test(word);

export const splitSentenceToWordsWithoutPunctuation = (sentence) => {
  return splitSentenceToWords(sentence).filter((word) => !isPunctuation(word));
};

export const simpleSplitSentenceToWordsWithoutPunctuation = (sentence) => {
  return simpleSplitSentenceToWords(sentence).filter((word) => !isPunctuation(word));
};

export const sentencesToCharacter = (text: string) => {
  return nlp(text).terms().out('array');
};

interface FieldMappingModel {
  keyField: string;
  valueField: string;
  childrenField: string;
}
export const mergeTextItems = <T = TextItemModel>(
  keyOfItemsToMerge: string[],
  sourceItems: T[],
  fieldMapping?: Partial<FieldMappingModel>,
  defaultFieldValue: Partial<T> = {}
): {
  items: T[];
  mergedItem: T;
} => {
  // why not map array with fieldMapping? => because we cannot control children field.
  // get mapping field to use in method
  const { keyField = 'key', valueField = 'value', childrenField = 'children' } = fieldMapping ?? {};
  const targetItems = cloneDeep(sourceItems);
  const itemsToMerge = sourceItems.filter((item) => keyOfItemsToMerge.includes(item[keyField]));

  const mergedValue = itemsToMerge.map((item) => item[valueField]).join(' ');
  const mergedItem: T | any = {
    [keyField]: nanoid(),
    [valueField]: mergedValue,
    [childrenField]: itemsToMerge,
    ...defaultFieldValue,
  };

  const firstItemIndex = sourceItems.findIndex(
    (item) => itemsToMerge[0][keyField] === item[keyField]
  );

  // remove items from first item index, maximum === itemsToMerge.length, then add new merged item to first index position
  targetItems.splice(firstItemIndex, itemsToMerge.length, mergedItem);

  return {
    items: targetItems,
    mergedItem,
  };
};

export const unmergeTextItems = <T = TextItemModel>(
  keyOfItemToUnmerge: string,
  sourceItems: T[],
  fieldMapping?: Partial<FieldMappingModel>
): {
  items: T[];
  unmergedItems: T[];
} => {
  // why not map array with fieldMapping? => because we cannot control children field.
  // get mapping field to use in method
  const { keyField = 'key', childrenField = 'children' } = fieldMapping ?? {};

  const targetItems = cloneDeep(sourceItems);
  const itemToUnmergeIndex = sourceItems.findIndex((item) => keyOfItemToUnmerge === item[keyField]);

  const unmergedItems: T[] = cloneDeep(sourceItems[itemToUnmergeIndex][childrenField]);

  // remove item from item need to unmerge index, maximum === 1, then add back children items to current index position
  targetItems.splice(itemToUnmergeIndex, 1, ...unmergedItems);

  return {
    items: targetItems,
    unmergedItems,
  };
};

export const sentencesToNode = (sentences: string) => {
  return sentencesToCharacter(sentences).map((char, index) => ({
    id: nanoid(),
    value: char,
    index,
  }));
};

// Convert Multiple sentences to single word list - with duplicate check.
export const splitMultipleSentencesToWords = (sentences: string[]): string[] => {
  return sentences
    .map((answer) => answer.split(' '))
    .reduce((result, splitWords) => {
      // lower case because ignore case when intersection
      const lowerCaseWords = splitWords.map((word) => word.toLowerCase());
      const lowerCaseResult = result.map((word) => word.toLowerCase());

      // get index of words that need to ignore when add to array
      const intersectionWordsIndex = intersection(lowerCaseWords, lowerCaseResult).map((word) =>
        lowerCaseWords.indexOf(word)
      );

      // filter words to distinct only
      const filteredSplitWords = splitWords.filter((_, index) => {
        return !intersectionWordsIndex.includes(index);
      });

      return [...result, ...filteredSplitWords];
    }, []);
};

export const mapSentenceWithAbbreviationToFullWord = (sentence) => {
  return sentence
    ?.split(' ')
    .map((eachWord) => {
      if (isAbbreviation(eachWord)) {
        return expandAbbreviationToFullWord(eachWord);
      }
      return eachWord;
    })
    .join(' ');
};

export const getSingleOrDoubleSentences = (arr) => {
  const usedDoubleIndex = new Set();
  const result = [];

  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];

    if (Number.isFinite(item.doubleBranchIndex)) {
      const key = item.doubleBranchIndex;

      // Nếu key chưa được xử lý
      if (!usedDoubleIndex.has(key)) {
        // Tìm phần tử thứ hai có cùng doubleBranchIndex
        const pair = arr.filter((x) => x.doubleBranchIndex === key);
        if (pair.length === 2) {
          result.push(pair);
          usedDoubleIndex.add(key);
        } else {
          // Trường hợp hiếm: chỉ có 1 phần tử có doubleBranchIndex
          result.push([item]);
          usedDoubleIndex.add(key);
        }
      }
      // Nếu đã xử lý rồi → bỏ qua
    } else {
      result.push([item]);
    }
  }
  return result;
};
